import { DataLayer, calcPrice } from "@grandvisionhq/analytics";
import { Locales } from "@grandvisionhq/state";
import { isDefined } from "@grandvisionhq/utils";
const getOrderLineItemSharedAttributes = (locale, lineItem, lineItemType, purchaseType) => {
  const localizedName = lineItem.product.name.map(DataLayer.DataLayerUtils.formatAttribute);
  const exclusiveBrand = "exclusiveBrand" in lineItem.product ? lineItem.product.exclusiveBrand : false;
  const isOnStock = "isOnStock" in lineItem.product ? lineItem.product.isOnStock : false;
  return {
    name: localizedName ? DataLayer.DataLayerUtils.transformValue(localizedName, locale) : "",
    id: lineItem.product.sku || "",
    price: `${calcPrice(lineItem?.price.unit.gross) ?? ""}`,
    dimension143: purchaseType ? purchaseType : DataLayer.DataLayerTypes.PurchaseType.OneTime,
    dimension144: lineItemType,
    dimension145: "true",
    dimension190: lineItem.product.erpId || "",
    dimension193: exclusiveBrand ? DataLayer.DataLayerTypes.BrandType.OwnBrand : DataLayer.DataLayerTypes.BrandType.ExternalBrand,
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock,
    metric140: `${calcPrice(lineItem?.price.unit.subtotal) || ""}` || "",
    metric141: `${calcPrice(lineItem?.price.unit.discount.amount) || ""}`,
    metric142: `${calcPrice(lineItem?.price?.unit.tax.amount) || ""}`,
    quantity: lineItem.quantity
  };
};
const getOrderLineItemSunglassesAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Single
  );
  const productMetaData = lineItem.product.additionalProductDetails;
  const frameShape = productMetaData?.frameShape?.map(DataLayer.DataLayerUtils.formatAttribute);
  const frameType = productMetaData?.frameType?.map(DataLayer.DataLayerUtils.formatAttribute);
  const faceShape = productMetaData?.faceShape?.map(DataLayer.DataLayerUtils.formatAttributes);
  const gender = productMetaData?.gender?.map(DataLayer.DataLayerUtils.formatAttributes);
  const frameMaterial = productMetaData?.frameMaterial?.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  const frameColor = productMetaData?.frameColor?.map(DataLayer.DataLayerUtils.formatAttributes);
  return {
    ...sharedAttributes,
    category: DataLayer.DataLayerTypes.Category.Sunglasses,
    brand: lineItem.product.brand || "",
    ...DataLayer.DataLayerUtils.transformToEventPayload({
      dimension147: frameColor,
      dimension16: gender,
      dimension146: frameMaterial,
      variant: frameShape,
      locale
    }),
    dimension191: frameType ? DataLayer.DataLayerUtils.transformValue(frameType, locale).toLowerCase() : "",
    dimension192: faceShape ? DataLayer.DataLayerUtils.transformValue(faceShape, locale).toLowerCase() : "",
    dimension140: lineItem.product.sku || ""
  };
};
const getOrderPrescriptionSunglassesAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Bundle
  );
  const category = DataLayer.DataLayerTypes.Category.Sunglasses;
  const name = lineItem.product?.name?.map(DataLayer.DataLayerUtils.formatAttribute);
  const frameType = lineItem.mainLineItem.product.frameType?.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  const faceShape = lineItem.mainLineItem.product.faceShape?.map(
    DataLayer.DataLayerUtils.formatAttributes
  );
  const isOnStock = lineItem.mainLineItem.product.isOnStock;
  const erpId = lineItem.mainLineItem.product.erpId;
  return {
    ...sharedAttributes,
    name: name ? DataLayer.DataLayerUtils.transformValue(name, locale) : "",
    brand: lineItem.product.brand || "",
    category,
    variant: "",
    dimension16: "",
    dimension146: "",
    dimension147: "",
    dimension140: lineItem.product.sku || "",
    dimension190: erpId || "",
    dimension191: frameType ? DataLayer.DataLayerUtils.transformValue(frameType, locale).toLowerCase() : "",
    dimension192: faceShape ? DataLayer.DataLayerUtils.transformValue(faceShape, locale).toLowerCase() : "",
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock
  };
};
const getOrderPrescriptionGlassesAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Bundle
  );
  const category = DataLayer.DataLayerTypes.Category.Optical;
  const name = lineItem.product?.name?.map(DataLayer.DataLayerUtils.formatAttribute);
  const frameType = lineItem.mainLineItem.product.frameType?.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  const faceShape = lineItem.mainLineItem.product.faceShape?.map(
    DataLayer.DataLayerUtils.formatAttributes
  );
  const isOnStock = lineItem.mainLineItem.product.isOnStock;
  const erpId = lineItem.mainLineItem.product.erpId;
  return {
    ...sharedAttributes,
    name: name ? DataLayer.DataLayerUtils.transformValue(name, locale) : "",
    brand: lineItem.product.brand || "",
    category,
    variant: "",
    dimension16: "",
    dimension146: "",
    dimension147: "",
    dimension140: lineItem.product.sku || "",
    dimension190: erpId || "",
    dimension191: frameType ? DataLayer.DataLayerUtils.transformValue(frameType, locale).toLowerCase() : "",
    dimension192: faceShape ? DataLayer.DataLayerUtils.transformValue(faceShape, locale).toLowerCase() : "",
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock
  };
};
const getOrderFrameAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Bundle
  );
  const category = DataLayer.DataLayerTypes.Category.Optical;
  const name = lineItem.product?.name?.map(DataLayer.DataLayerUtils.formatAttribute);
  const additionalProductDetails = lineItem.product.additionalProductDetails;
  const frameType = additionalProductDetails?.frameType?.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  const faceShape = additionalProductDetails?.faceShape?.map(
    DataLayer.DataLayerUtils.formatAttributes
  );
  const isOnStock = lineItem.product.isOnStock;
  const erpId = lineItem.product.erpId;
  return {
    ...sharedAttributes,
    name: name ? DataLayer.DataLayerUtils.transformValue(name, locale) : "",
    brand: lineItem.product.brand || "",
    category,
    variant: "",
    dimension16: "",
    dimension146: "",
    dimension147: "",
    dimension140: lineItem.product.sku || "",
    dimension190: erpId || "",
    dimension191: frameType ? DataLayer.DataLayerUtils.transformValue(frameType, locale).toLowerCase() : "",
    dimension192: faceShape ? DataLayer.DataLayerUtils.transformValue(faceShape, locale).toLowerCase() : "",
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock
  };
};
const getOrderNonPrescriptionBundleAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Bundle
  );
  const category = DataLayer.DataLayerTypes.Category.Optical;
  const name = lineItem.product?.name?.map(DataLayer.DataLayerUtils.formatAttribute);
  const isOnStock = lineItem.product.isOnStock;
  const erpId = lineItem.product.erpId;
  return {
    ...sharedAttributes,
    name: name ? DataLayer.DataLayerUtils.transformValue(name, locale) : "",
    brand: lineItem.product.brand || "",
    category,
    variant: "",
    dimension16: "",
    dimension146: "",
    dimension147: "",
    dimension140: lineItem.product.sku || "",
    dimension190: erpId || "",
    dimension191: "",
    dimension192: "",
    dimension194: isOnStock ? DataLayer.DataLayerTypes.Stock.InStock : DataLayer.DataLayerTypes.Stock.OutOfStock
  };
};
const getOrderLineItemReadyReadersAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Single
  );
  const productMetaData = lineItem.product.additionalProductDetails;
  const frameShape = productMetaData?.frameShape?.map(DataLayer.DataLayerUtils.formatAttribute);
  const frameType = productMetaData?.frameType?.map(DataLayer.DataLayerUtils.formatAttribute);
  const faceShape = productMetaData?.faceShape?.map(DataLayer.DataLayerUtils.formatAttributes);
  const gender = productMetaData?.gender?.map(DataLayer.DataLayerUtils.formatAttributes);
  const frameMaterial = productMetaData?.frameMaterial?.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  const frameColor = productMetaData?.frameColor?.map(DataLayer.DataLayerUtils.formatAttributes);
  return {
    ...sharedAttributes,
    brand: lineItem.product.brand || "",
    category: DataLayer.DataLayerTypes.Category.ReadyReaders,
    ...DataLayer.DataLayerUtils.transformToEventPayload({
      dimension147: frameColor,
      dimension16: gender,
      dimension146: frameMaterial,
      variant: frameShape,
      locale
    }),
    dimension140: lineItem.product.sku || "",
    dimension191: frameType ? DataLayer.DataLayerUtils.transformValue(frameType, locale).toLowerCase() : "",
    dimension192: faceShape ? DataLayer.DataLayerUtils.transformValue(faceShape, locale).toLowerCase() : ""
  };
};
const getOrderLineItemAccessoriesAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Single
  );
  const productMetaData = lineItem.product.additionalProductDetails;
  const accessoryType = productMetaData?.accessoryType?.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  const gender = productMetaData?.gender?.map(DataLayer.DataLayerUtils.formatAttributes);
  const material = productMetaData?.material?.map(DataLayer.DataLayerUtils.formatAttribute);
  const accessoryColor = productMetaData?.accessoryColor?.map(
    DataLayer.DataLayerUtils.formatAttributes
  );
  return {
    ...sharedAttributes,
    category: DataLayer.DataLayerTypes.Category.Accessories,
    brand: lineItem.product.brand || "",
    ...DataLayer.DataLayerUtils.transformToEventPayload({
      variant: accessoryType,
      dimension146: material,
      dimension16: gender,
      dimension147: accessoryColor,
      locale
    }),
    dimension140: lineItem.product.sku || ""
  };
};
const getOrderLineItemSolutionsAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    DataLayer.DataLayerTypes.LineItemType.Single
  );
  return {
    ...sharedAttributes,
    category: DataLayer.DataLayerTypes.Category.Solutions,
    brand: lineItem.product.brand || "",
    variant: "solution"
  };
};
const getOrderLineItemContactLensesAttributes = (locale, lineItem) => {
  const sharedAttributes = getOrderLineItemSharedAttributes(
    locale,
    lineItem,
    lineItem.isSubscription ? DataLayer.DataLayerTypes.LineItemType.Subscription : DataLayer.DataLayerTypes.LineItemType.Single,
    lineItem.isSubscription ? DataLayer.DataLayerTypes.PurchaseType.Subscription : DataLayer.DataLayerTypes.PurchaseType.OneTime
  );
  const lenseType = lineItem.product?.lensType?.map(DataLayer.DataLayerUtils.formatAttribute);
  const material = lineItem.product?.material?.map(DataLayer.DataLayerUtils.formatAttribute);
  const replacementFrequency = lineItem.product?.replacementFrequency.map(
    DataLayer.DataLayerUtils.formatAttribute
  );
  return {
    ...sharedAttributes,
    brand: lineItem.product.brand || "",
    category: DataLayer.DataLayerTypes.Category.ContactLenses,
    variant: lenseType ? DataLayer.DataLayerUtils.transformValue(lenseType, locale) : "",
    dimension148: replacementFrequency ? DataLayer.DataLayerUtils.transformValue(replacementFrequency, locale) : "",
    dimension146: material ? DataLayer.DataLayerUtils.transformValue(material, locale) : "",
    dimension140: lineItem.product.sku || ""
  };
};
const orderLineItemToProductItem = (lineItem) => {
  const productType = lineItem.product.currentProduct?.type.toLowerCase();
  if (!productType) {
    return;
  }
  switch (productType) {
    case "sunglasses": {
      if (lineItem.__typename == "OrderLineItemRegular") {
        return getOrderLineItemSunglassesAttributes(Locales["en-GB"], lineItem);
      } else if (lineItem.__typename === "OrderLineItemPrescriptionGlass") {
        return getOrderPrescriptionSunglassesAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
    case "contact-lenses":
    case "contact_lenses": {
      if (lineItem.__typename === "OrderLineItemContactLens") {
        return getOrderLineItemContactLensesAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
    case "solutions": {
      if (lineItem.__typename === "OrderLineItemRegular" || lineItem.__typename === "OrderLineItemSolution") {
        return getOrderLineItemSolutionsAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
    case "ready_readers": {
      if (lineItem.__typename === "OrderLineItemRegular") {
        return getOrderLineItemReadyReadersAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
    case "accessories": {
      if (lineItem.__typename === "OrderLineItemRegular") {
        return getOrderLineItemAccessoriesAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
    case "frames": {
      if (lineItem.__typename === "OrderLineItemPrescriptionGlass") {
        return getOrderPrescriptionGlassesAttributes(Locales["en-GB"], lineItem);
      } else if (lineItem.__typename === "OrderLineItemNonPrescriptionBundle") {
        return getOrderNonPrescriptionBundleAttributes(Locales["en-GB"], lineItem);
      } else if (lineItem.__typename === "OrderLineItemRegular") {
        return getOrderFrameAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
    case "non_prescription_bundle": {
      if (lineItem.__typename === "OrderLineItemNonPrescriptionBundle") {
        return getOrderNonPrescriptionBundleAttributes(Locales["en-GB"], lineItem);
      } else {
        break;
      }
    }
  }
  console.error(`No analytics mapper found for line-item: `, lineItem);
};
const mapOrderLineItemToDataLayerProductObjects = (lineItems) => {
  return lineItems?.map(orderLineItemToProductItem).filter(isDefined) || [];
};
export {
  getOrderLineItemSharedAttributes,
  mapOrderLineItemToDataLayerProductObjects
};
//# sourceMappingURL=data-layer.js.map
